// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Tooltip, { TooltipPresentationProps } from "@stoneage/MobiusOverrides/Tooltip/Tooltip";
import { mergeWithCss, xParserOptions } from "@stoneage/XDL/Utilities";
import parse from "html-react-parser";
import React from "react";
import { useState } from "react";

interface Props {
    value: boolean;
    options: { value: boolean; label: string }[];
    onChange: any;
    label?: string;
    extendedStyles?: JettingAppToggleStyles;
    tooltipText?: string;
    message?: { message: string; type: string };
    readonly?: boolean;
}

export interface JettingAppToggleStyles {
    wrapper?: InjectableCss;
    button?: ClickablePresentationProps;
    container?: InjectableCss;
    label?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
    error?: TypographyPresentationProps;
}

export const jettingAppToggleStyles: JettingAppToggleStyles = {};

const JettingAppToggle = ({
    extendedStyles,
    onChange,
    value,
    options,
    label,
    tooltipText,
    message,
    readonly,
}: Props) => {
    const [styles] = useState(() => mergeWithCss(jettingAppToggleStyles, extendedStyles));

    const setSelectedValue = (selectedOption: boolean) => {
        if (value !== selectedOption) {
            onChange(selectedOption);
        }
    };
    if (!options) {
        return null;
    }

    const textLabel = tooltipText ? (
        <>
            <Typography {...styles.label}>
                {label}
                <Tooltip {...styles.tooltip} passAsChildren={true}>
                    <Typography>{parse(tooltipText, xParserOptions)}</Typography>
                </Tooltip>
            </Typography>
        </>
    ) : (
        <Typography {...styles.label}>{label}</Typography>
    );

    return (
        <StyledWrapper {...styles.container}>
            {(label || tooltipText) && textLabel}
            <StyledWrapper {...styles.wrapper}>
                {options.map(option => {
                    return (
                        <Clickable
                            {...styles.button}
                            onClick={() => setSelectedValue(option.value)}
                            className={value === option.value ? "selected" : ""}
                            disabled={value === option.value || readonly}
                            key={option.label}
                            translate="no"
                        >
                            {option.label}
                        </Clickable>
                    );
                })}
            </StyledWrapper>
            {message?.message && (
                <Typography {...styles.error} color={message?.type?.toLowerCase() === "error" ? "danger" : "warning"}>
                    {message.message}
                </Typography>
            )}
        </StyledWrapper>
    );
};
export default JettingAppToggle;
